import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useCheckBatchDetails } from "@/hooks/useCheckBatches";
import { useFinalizeBatch } from "@/hooks/useFinalizeBatch";
import { useGenerateChecks } from "@/hooks/useGenerateChecks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckSquare, FileText, AlertCircle, Eye } from "lucide-react";

// Reusable components
import BatchSummaryCard from "@/components/check-batches/BatchSummaryCard";
import BatchControls from "@/components/check-batches/BatchControls";
import { useDeleteCheckBatch } from "@/hooks/useCheckBatches";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import PaymentCheckTable from "@/components/check-batches/PaymentCheckTable";
import PaymentCheckDetails from "@/components/check-batches/PaymentCheckDetails";
import { Loading } from "@/components/ui/loading";
import ErrorState from "@/components/ui/ErrorState";
import { EmptyState } from "@/components/brand/EmptyState";
import { ReceiptText } from "lucide-react";

// ...existing code...

const BatchDetails: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const {
    mutate: deleteBatch,
    status: deleteStatus,
    error: deleteError,
  } = useDeleteCheckBatch(batchId ?? "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);

  const {
    data: batch,
    isLoading: batchLoading,
    error: batchError,
  } = useCheckBatchDetails(batchId ?? "");

  // Inject batchStatus into each check for row actions
  const paymentChecks = (batch?.paymentChecks || []).map((check) => ({
    ...check,
    batchStatus: batch.status,
  }));
  const selectedCheck =
    paymentChecks.find((c) => c.id === selectedCheckId) || null;

  // Mutations for batch actions
  const {
    mutate: generateChecks,
    status: generateStatus,
    error: generateError,
  } = useGenerateChecks(batchId ?? "");
  const {
    mutate: finalizeBatch,
    status: finalizeStatus,
    error: finalizeError,
  } = useFinalizeBatch(batchId ?? "");

  if (batchLoading) {
    return <Loading text="Loading batch details..." size="default" />;
  }
  if (batchError || !batch) {
    return (
      <ErrorState
        message={batchError?.message || "Failed to load batch details."}
      />
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header with gradient and icon */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-primary/20">
            <CheckSquare className="h-6 w-6 text-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Batch Details</h1>
        </div>
        <p className="text-muted-foreground">
          Manage and review payment check batches for events
        </p>
      </div>

      {/* Batch Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Batch Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BatchSummaryCard batch={batch} />
        </CardContent>
      </Card>

      {/* Batch Controls Card */}
      <Card>
        <CardHeader>
          <CardTitle>Batch Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <BatchControls
            status={batch.status}
            onFinalize={() => finalizeBatch()}
            onDelete={() => setShowDeleteModal(true)}
            disabled={
              generateStatus === "pending" ||
              finalizeStatus === "pending" ||
              deleteStatus === "pending"
            }
          />
          <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Delete Batch</DialogTitle>
              </DialogHeader>
              <div className="my-4">
                <p className="text-destructive font-semibold mb-2">
                  Are you sure you want to delete this batch?
                </p>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All payment checks and data
                  associated with this batch will be permanently deleted.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteStatus === "pending"}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    await deleteBatch();
                    setShowDeleteModal(false);
                    navigate(-1);
                  }}
                  disabled={deleteStatus === "pending"}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {(generateError || finalizeError) && (
            <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Error</span>
              </div>
              <p className="text-sm text-destructive/80 mt-1">
                {generateError?.message || finalizeError?.message}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Checks Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Checks</CardTitle>
        </CardHeader>
        <CardContent>
          {paymentChecks.length > 0 ? (
            <PaymentCheckTable
              checks={paymentChecks}
              onSelectCheck={setSelectedCheckId}
            />
          ) : (
            <EmptyState
              icon={ReceiptText}
              title="No payment checks"
              description="Checks appear here once this batch has been generated."
            />
          )}
        </CardContent>
      </Card>

      {/* Payment Check Details Modal */}
      {selectedCheck && (
        <PaymentCheckDetails
          check={selectedCheck}
          onClose={() => setSelectedCheckId(null)}
        />
      )}
    </div>
  );
};

export default BatchDetails;
